import React, { useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons'
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole
} from '@floating-ui/react'

// A notification's ⋯ menu (owner 2026-10-24: row actions in "hover or
// focus actions plus a ⋯ menu, keyboard reachable"). A real menu button:
// named, aria-haspopup/expanded, Enter / Space / ArrowDown open it with the
// first item focused, the arrows move through the items (wrapping), Escape
// or a click outside closes it and focus returns to the button. The shared
// ⋮ (shared/menu) has items a keyboard can't reach, hence this one.
//
// items: [{ key, label, Icon, onSelect, disabled, reason }]. A disabled item
// stays in the list (focusable, aria-disabled) with its reason as its
// description, so the keyboard reaches why.
//
// Portalled to <body>, so the menu carries the workspace's tokens itself
// (.sw-workspace) to follow the theme.
export const InboxRowMenu = ({ label, items, onOpenChange }) => {
  const [open, setOpenState] = useState(false)
  const setOpen = value => {
    setOpenState(value)
    if (onOpenChange) onOpenChange(value)
  }
  const [activeIndex, setActiveIndex] = useState(null)
  const listRef = useRef([])

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })
  // disabledIndices: none -- an unavailable item is still reached by the
  // arrows (floating-ui skips aria-disabled items by default), so its reason is.
  const navigation = useListNavigation(context, { listRef, activeIndex, onNavigate: setActiveIndex, loop: true, disabledIndices: [] })
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, role, navigation])

  const select = item => {
    if (item.disabled) return
    setOpen(false)
    item.onSelect && item.onSelect()
  }

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        className="btn nt-icon-btn nt-more"
        aria-label={label}
        title={label}
        {...getReferenceProps({ onClick: e => e.stopPropagation() })}>
        <FontAwesomeIcon icon={faEllipsis} />
      </button>
      {open &&
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={0}>
            <div ref={refs.setFloating} style={floatingStyles} className="sw-workspace nt-menu" aria-label={label} {...getFloatingProps()}>
              {items.map((item, index) => {
                const Icon = item.Icon
                const reasonID = item.disabled && item.reason ? `nt-menu-reason-${item.key}` : undefined
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className="nt-menu-item"
                    tabIndex={activeIndex === index ? 0 : -1}
                    aria-disabled={item.disabled ? 'true' : undefined}
                    aria-describedby={reasonID}
                    title={item.disabled ? item.reason : undefined}
                    ref={node => {
                      listRef.current[index] = node
                    }}
                    {...getItemProps({
                      onClick: e => {
                        e.stopPropagation()
                        select(item)
                      }
                    })}>
                    {Icon && <span className="nt-menu-icon" aria-hidden="true"><Icon /></span>}
                    <span>{item.label}</span>
                  </button>
                )
              })}
              {items.filter(item => item.disabled && item.reason).map(item =>
                <span key={item.key} id={`nt-menu-reason-${item.key}`} className="visually-hidden">{item.reason}</span>)}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>}
    </>
  )
}
